/**
 * Page Parametres : gestion des acces clients et des invitations.
 * - Onglet "Clients" : liste des comptes clients actifs
 * - Onglet "Invitations" : liste des invitations avec bouton Renvoyer / Revoquer
 * - Onglet "Collaborateurs" : gestion des comptes internes (admin/ceo seulement)
 * - Bouton "Creer un acces client" ouvre la modal CreateClientAccessModal
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CreateClientAccessModal from './components/CreateClientAccessModal';
import DriveRestructurePanel from './components/DriveRestructurePanel';
import DriveBranchRestructurePanel from './components/DriveBranchRestructurePanel';
import DriveClientMergePanel from './components/DriveClientMergePanel';
import TeamPermissionsPanel from './components/TeamPermissionsPanel';
import ClientsRelinkPanel from './components/ClientsRelinkPanel';
import { useAuth } from './App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; }
};

const formatDateTime = (iso) => {
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
};

const isExpired = (iso) => {
  try { return new Date(iso) < new Date(); } catch { return false; }
};

const ParametresPage = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'ceo'].includes(user?.role);

  const [tab, setTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [permsConfig, setPermsConfig] = useState(null);
  const [editCollab, setEditCollab] = useState(null);
  const [showCreateCollab, setShowCreateCollab] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [linkToShow, setLinkToShow] = useState(null);

  // Theme (light/dark/system)
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('realease_theme') || 'system'; } catch { return 'system'; }
  });

  const applyTheme = (mode) => {
    const root = document.documentElement;
    let resolved = mode;
    if (mode === 'system') {
      resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    if (resolved === 'dark') root.classList.add('dark'); else root.classList.remove('dark');
  };

  const handleThemeChange = (mode) => {
    setTheme(mode);
    try { localStorage.setItem('realease_theme', mode); } catch {}
    applyTheme(mode);
  };

  useEffect(() => { applyTheme(theme); }, [theme]);

  const fetchClients = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/client-users`);
      setClients(r.data || []);
    } catch {}
  }, []);

  const fetchInvitations = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/invitations`);
      setInvitations(r.data || []);
    } catch {}
  }, []);

  const fetchCollaborators = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [r, p] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/permissions`).catch(() => ({ data: null })),
      ]);
      setCollaborators((r.data || []).filter(u => u.role !== 'client'));
      setPermsConfig(p.data);
    } catch {}
  }, [isAdmin]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchClients(), fetchInvitations(), fetchCollaborators()]);
    setLoading(false);
  }, [fetchClients, fetchInvitations, fetchCollaborators]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const resend = async (invite) => {
    try {
      const r = await axios.post(`${API}/invitations/${invite.invite_id}/resend`);
      setLinkToShow({ email: invite.email, link: r.data.magic_link, action: 'resend' });
      fetchInvitations();
    } catch (e) {
      alert(e.response?.data?.detail || 'Echec envoi');
    }
  };

  const revoke = async (invite) => {
    if (!window.confirm(`Revoquer l'invitation de ${invite.email} ?`)) return;
    try {
      await axios.delete(`${API}/invitations/${invite.invite_id}`);
      fetchInvitations();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteClient = async (client) => {
    if (!window.confirm(`Supprimer DEFINITIVEMENT le compte de ${client.name} (${client.email}) ?\n\nCette action :\n- Supprime le compte (login impossible)\n- Dissocie le client de ses leads/projets (qui restent en base)\n- Annule les invitations en attente sur cet email\n- Vous permet de recreer un acces avec le meme email ensuite\n\nAction irreversible.`)) return;
    try {
      await axios.delete(`${API}/admin/clients/${client.user_id}`);
      fetchClients();
      fetchInvitations();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur suppression compte client');
    }
  };

  const copyLink = async (link) => {
    try { await navigator.clipboard.writeText(link); alert('Lien copie'); } catch {}
  };

  return (
    <div className="p-4 lg:p-8" data-testid="parametres-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Parametres & Acces</h1>
            <p className="text-gray-500 mt-1">Gerez les acces clients et les invitations.</p>
          </div>
          <button onClick={() => setModalOpen(true)}
                  className="px-4 py-2 bg-[#008A8D] text-white rounded-lg font-semibold hover:bg-[#006B6D] flex items-center gap-2"
                  data-testid="new-client-access-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
            Creer un acces client
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200 mb-6" data-testid="param-tabs">
          <TabBtn active={tab === 'clients'} onClick={() => setTab('clients')} label={`Clients (${clients.length})`} testid="tab-clients" />
          <TabBtn active={tab === 'invitations'} onClick={() => setTab('invitations')} label={`Invitations (${invitations.length})`} testid="tab-invitations" />
          {isAdmin && (
            <TabBtn active={tab === 'collaborators'} onClick={() => setTab('collaborators')} label={`Collaborateurs (${collaborators.length})`} testid="tab-collaborators" />
          )}
          {isAdmin && (
            <>
            <TabBtn active={tab === 'maintenance'} onClick={() => setTab('maintenance')} label="Maintenance" testid="tab-maintenance" />
            <TabBtn active={tab === 'drive'} onClick={() => setTab('drive')} label="Drive" testid="tab-drive" />
            <TabBtn active={tab === 'equipe'} onClick={() => setTab('equipe')} label="Equipe &amp; Permissions" testid="tab-equipe" />
            </>
          )}
          <TabBtn active={tab === 'apparence'} onClick={() => setTab('apparence')} label="Apparence" testid="tab-apparence" />
        </div>

        {loading && <p className="text-xs text-gray-400 italic">Chargement...</p>}

        {/* Clients tab */}
        {tab === 'clients' && (
          <div className="realease-card overflow-hidden" data-testid="clients-table">
            {clients.length === 0 ? (
              <p className="p-8 text-center text-gray-400 italic">Aucun client actif pour le moment.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left">
                    <th className="p-3 font-semibold">Nom</th>
                    <th className="p-3 font-semibold">Email</th>
                    <th className="p-3 font-semibold">Cree le</th>
                    <th className="p-3 font-semibold">Projet lie</th>
                    <th className="p-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(c => (
                    <tr key={c.user_id} className="border-t border-gray-100" data-testid={`client-${c.user_id}`}>
                      <td className="p-3 font-medium text-gray-900">{c.name}</td>
                      <td className="p-3 text-gray-700 font-mono text-xs">{c.email}</td>
                      <td className="p-3 text-gray-500 text-xs">{formatDate(c.created_at)}</td>
                      <td className="p-3 text-xs">
                        {c.linked_project_id ? (
                          <a href={`/projects/${c.linked_project_id}`} className="text-[#008A8D] hover:underline">Voir le projet</a>
                        ) : c.linked_lead_id ? (
                          <a href={`/qualification/${c.linked_lead_id}`} className="text-[#A57F4F] hover:underline">Voir la qualif</a>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <button
                          onClick={() => deleteClient(c)}
                          className="text-xs px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg font-medium"
                          data-testid={`delete-client-${c.user_id}`}
                          title="Supprimer le compte client (permet de recreer un acces avec le meme email)"
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Invitations tab */}
        {tab === 'invitations' && (
          <div className="realease-card overflow-hidden" data-testid="invitations-table">
            {invitations.length === 0 ? (
              <p className="p-8 text-center text-gray-400 italic">Aucune invitation en attente.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left">
                    <th className="p-3 font-semibold">Destinataire</th>
                    <th className="p-3 font-semibold">Statut</th>
                    <th className="p-3 font-semibold">Expire le</th>
                    <th className="p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map(i => {
                    const used = i.used;
                    const expired = !used && isExpired(i.expires_at);
                    const active = !used && !expired;
                    return (
                      <tr key={i.invite_id} className="border-t border-gray-100" data-testid={`invite-${i.invite_id}`}>
                        <td className="p-3">
                          <div className="font-medium text-gray-900">{i.client_name}</div>
                          <div className="text-xs text-gray-500 font-mono">{i.email}</div>
                        </td>
                        <td className="p-3">
                          {used && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">Utilisee</span>}
                          {expired && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Expiree</span>}
                          {active && <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">En attente</span>}
                        </td>
                        <td className="p-3 text-xs text-gray-500">{formatDateTime(i.expires_at)}</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            {active && (
                              <button onClick={() => copyLink(`${window.location.origin}/register?token=${i.token}`)}
                                      className="text-xs font-semibold text-[#008A8D] hover:underline"
                                      data-testid={`copy-${i.invite_id}`}>
                                Copier lien
                              </button>
                            )}
                            {!used && (
                              <button onClick={() => resend(i)}
                                      className="text-xs font-semibold text-[#A57F4F] hover:underline"
                                      data-testid={`resend-${i.invite_id}`}>
                                {expired ? 'Renouveler' : 'Renvoyer'}
                              </button>
                            )}
                            <button onClick={() => revoke(i)}
                                    className="text-xs font-semibold text-red-500 hover:underline"
                                    data-testid={`revoke-${i.invite_id}`}>
                              Revoquer
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Collaborators tab (admin only) */}
        {tab === 'collaborators' && isAdmin && (
          <div className="realease-card overflow-hidden" data-testid="collabs-table">
            <div className="p-4 bg-[#FDF8F0] border-b border-[#E8DCC4] flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-[#A57F4F] font-semibold">Gestion des comptes internes (Julie, Carla, Amelie, CEO...)</p>
              <button onClick={() => setShowCreateCollab(true)}
                      className="px-3 py-1.5 bg-[#008A8D] text-white text-xs font-semibold rounded-lg hover:bg-[#006B6D]"
                      data-testid="new-collab-btn">
                + Nouveau collaborateur
              </button>
            </div>
            {collaborators.length === 0 ? (
              <p className="p-8 text-center text-gray-400 italic">Aucun collaborateur.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left">
                    <th className="p-3 font-semibold">Nom</th>
                    <th className="p-3 font-semibold">Email</th>
                    <th className="p-3 font-semibold">Role</th>
                    <th className="p-3 font-semibold">Permissions</th>
                    <th className="p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {collaborators.map(c => (
                    <tr key={c.user_id} className="border-t border-gray-100" data-testid={`collab-row-${c.user_id}`}>
                      <td className="p-3 font-medium">{c.name}</td>
                      <td className="p-3 text-gray-700 font-mono text-xs">{c.email}</td>
                      <td className="p-3"><span className="px-2 py-0.5 bg-[#008A8D]/10 text-[#006B6E] rounded-full text-xs font-semibold">{c.role}</span></td>
                      <td className="p-3 text-xs text-gray-500 max-w-xs truncate">{(c.permissions || []).join(', ') || '-'}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <button onClick={() => setEditCollab(c)} className="text-xs font-semibold text-[#008A8D] hover:underline" data-testid={`edit-collab-${c.user_id}`}>Modifier</button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Reinitialiser les permissions de ${c.name || c.email} aux valeurs par defaut du role "${c.role}" ?`)) return;
                              try {
                                await axios.post(`${API}/admin/users/${c.user_id}/reset-permissions`);
                                await refreshAll();
                              } catch (e) {
                                alert(e.response?.data?.detail || 'Echec reinitialisation');
                              }
                            }}
                            className="text-xs font-semibold text-[#A57F4F] hover:underline"
                            data-testid={`reset-perms-${c.user_id}`}
                          >
                            Reinitialiser permissions
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Maintenance tab (admin/ceo) */}
      {tab === 'maintenance' && isAdmin && (
        <MaintenancePanel />
      )}

      {/* Drive tab (CEO / Admin) */}
      {tab === 'drive' && isAdmin && (
        <div className="max-w-5xl mx-auto -mt-4 px-8 space-y-6" data-testid="drive-section">
          <ClientsRelinkPanel />
          <DriveBranchRestructurePanel />
          <DriveRestructurePanel />
          <DriveClientMergePanel />
        </div>
      )}

      {/* Equipe & Permissions tab (CEO / Admin) */}
      {tab === 'equipe' && isAdmin && (
        <div className="max-w-5xl mx-auto -mt-4 px-8" data-testid="equipe-section">
          <TeamPermissionsPanel />
        </div>
      )}

      {/* Apparence tab */}
      {tab === 'apparence' && (
        <div className="max-w-3xl mx-auto -mt-4 px-8" data-testid="apparence-section">
          <div className="realease-card p-6">
            <h3 className="font-semibold text-gray-900 mb-2">Theme de l'application</h3>
            <p className="text-sm text-gray-500 mb-5">Choisissez l'apparence claire, sombre ou suivez votre systeme.</p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: 'light', label: 'Clair', icon: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z' },
                { id: 'dark', label: 'Sombre', icon: 'M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z' },
                { id: 'system', label: 'Systeme', icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
              ].map(opt => (
                <button
                  key={opt.id}
                  onClick={() => handleThemeChange(opt.id)}
                  className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${theme === opt.id ? 'border-[#008A8D] bg-[#008A8D]/5' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid={`theme-option-${opt.id}`}
                >
                  <svg className={`w-6 h-6 ${theme === opt.id ? 'text-[#008A8D]' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={opt.icon} />
                  </svg>
                  <span className={`text-sm font-semibold ${theme === opt.id ? 'text-[#008A8D]' : 'text-gray-700'}`}>{opt.label}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 italic mt-4">Le theme est sauvegarde sur cet appareil. Le mode sombre couvre l'interface principale ; certains modules (PDF) restent toujours en clair pour l'impression.</p>
          </div>
        </div>
      )}

      <CreateClientAccessModal open={modalOpen}
                               onClose={() => setModalOpen(false)}
                               onCreated={() => { refreshAll(); setModalOpen(false); }} />

      {editCollab && (
        <EditCollabModal collab={editCollab} permsConfig={permsConfig || { permissions: [], default_by_role: {} }}
          onClose={() => setEditCollab(null)}
          onSuccess={() => { setEditCollab(null); fetchCollaborators(); }} />
      )}
      {showCreateCollab && (
        <CreateCollabModal permsConfig={permsConfig || { permissions: [], default_by_role: {} }}
          onClose={() => setShowCreateCollab(false)}
          onSuccess={() => { setShowCreateCollab(false); fetchCollaborators(); }} />
      )}

      {/* Mini modal pour afficher le lien apres resend */}
      {linkToShow && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setLinkToShow(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold mb-2">Nouveau lien genere</h3>
            <p className="text-xs text-gray-500 mb-3">Le lien a ete envoye a {linkToShow.email}. Vous pouvez aussi le copier :</p>
            <div className="p-3 bg-gray-50 rounded-lg break-all font-mono text-xs mb-3">{linkToShow.link}</div>
            <div className="flex gap-2">
              <button onClick={() => copyLink(linkToShow.link)} className="flex-1 px-3 py-2 border border-[#008A8D] text-[#008A8D] rounded-lg text-sm font-semibold">Copier</button>
              <button onClick={() => setLinkToShow(null)} className="flex-1 px-3 py-2 bg-[#008A8D] text-white rounded-lg text-sm font-semibold">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TabBtn = ({ active, onClick, label, testid }) => (
  <button onClick={onClick}
          className={`px-4 py-2.5 text-sm font-semibold transition-all -mb-px ${active ? 'border-b-2 border-[#008A8D] text-[#008A8D]' : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid={testid}>
    {label}
  </button>
);

// ============ Modales collaborateurs (Admin/CEO) ============

const ROLE_OPTIONS = [
  { value: 'collaborateur', label: 'Collaborateur' },
  { value: 'carla', label: 'CRM / Qualification' },
  { value: 'julie', label: 'Gestion Projets' },
  { value: 'amelie', label: 'Gestion Delais' },
  { value: 'ceo', label: 'CEO / Direction' },
  { value: 'admin', label: 'Administrateur' },
];

const PermsCheckboxes = ({ allPerms, selected, onToggle }) => (
  <div className="grid grid-cols-2 gap-2 mt-1">
    {Object.entries(allPerms).map(([key, label]) => (
      <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox"
               checked={selected.includes(key)}
               onChange={() => onToggle(key)}
               className="w-4 h-4 rounded text-[#008A8D]"
               data-testid={`perm-${key}`} />
        <span className="truncate">{label}</span>
      </label>
    ))}
  </div>
);

const EditCollabModal = ({ collab, permsConfig, onClose, onSuccess }) => {
  const allPerms = permsConfig?.permissions || {};
  const [form, setForm] = useState({ name: collab.name || '', role: collab.role || 'collaborateur', permissions: collab.permissions || [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const togglePerm = (perm) => {
    setForm(prev => ({ ...prev, permissions: prev.permissions.includes(perm) ? prev.permissions.filter(p => p !== perm) : [...prev.permissions, perm] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      await axios.put(`${API}/admin/users/${collab.user_id}`, { name: form.name, role: form.role, permissions: form.permissions });
      onSuccess();
    } catch (err) { setError(err.response?.data?.detail || 'Erreur lors de la mise a jour'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="edit-collab-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Modifier {collab.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="text-xs font-semibold text-gray-600">Nom</label>
            <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required data-testid="collab-name" />
          </div>
          <div><label className="text-xs font-semibold text-gray-600">Email</label>
            <input type="email" value={collab.email} disabled className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-500" />
          </div>
          <div><label className="text-xs font-semibold text-gray-600">Role</label>
            <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="collab-role">
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div><label className="text-xs font-semibold text-gray-600">Permissions</label>
            <PermsCheckboxes allPerms={allPerms} selected={form.permissions} onToggle={togglePerm} />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-3 bg-[#008A8D] text-white rounded-lg font-medium disabled:opacity-50" data-testid="collab-save">
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateCollabModal = ({ permsConfig, onClose, onSuccess }) => {
  const allPerms = permsConfig?.permissions || {};
  const defaults = permsConfig?.default_by_role || {};
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'collaborateur', permissions: [], branch: 'bretagne', cross_branch_access: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const setRole = (role) => setForm(prev => ({ ...prev, role, permissions: defaults[role] || [] }));
  const togglePerm = (perm) => setForm(prev => ({ ...prev, permissions: prev.permissions.includes(perm) ? prev.permissions.filter(p => p !== perm) : [...prev.permissions, perm] }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      await axios.post(`${API}/admin/users`, form);
      onSuccess();
    } catch (err) { setError(err.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-collab-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Nouveau collaborateur</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="text-xs font-semibold text-gray-600">Nom</label>
            <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required />
          </div>
          <div><label className="text-xs font-semibold text-gray-600">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required />
          </div>
          <div><label className="text-xs font-semibold text-gray-600">Mot de passe</label>
            <input type="text" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required />
          </div>
          <div><label className="text-xs font-semibold text-gray-600">Role</label>
            <select value={form.role} onChange={e => setRole(e.target.value)} className="w-full px-3 py-2 border rounded-lg" data-testid="new-collab-role">
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600">Branche</label>
              <select value={form.branch} onChange={e => setForm({...form, branch: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="new-collab-branch">
                <option value="bretagne">Bretagne</option>
                <option value="nord">Nord</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
                <input type="checkbox" checked={form.cross_branch_access}
                       onChange={e => setForm({...form, cross_branch_access: e.target.checked})}
                       className="w-4 h-4 rounded text-[#008A8D]"
                       data-testid="new-collab-cross-branch" />
                <span>Acces inter-branches (groupe)</span>
              </label>
            </div>
          </div>
          <div><label className="text-xs font-semibold text-gray-600">Permissions</label>
            <PermsCheckboxes allPerms={allPerms} selected={form.permissions} onToggle={togglePerm} />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-3 bg-[#008A8D] text-white rounded-lg font-medium disabled:opacity-50">
              {loading ? 'Creation...' : 'Creer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

function MaintenancePanel() {
  const [auditing, setAuditing] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [projectsAuditing, setProjectsAuditing] = useState(false);
  const [projectsReport, setProjectsReport] = useState(null);
  const [projectsError, setProjectsError] = useState('');

  const runAudit = async () => {
    if (!window.confirm("Lancer l'audit Drive : verifier que tous les dossiers clients ont bien les sous-dossiers 'Doc admins clients' et 'Projets', et creer ceux qui manquent ?")) return;
    setAuditing(true);
    setError('');
    setReport(null);
    try {
      const r = await axios.post(`${API}/drive/clients/audit-repair`);
      setReport(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur audit');
    } finally {
      setAuditing(false);
    }
  };

  const runProjectsAudit = async () => {
    if (!window.confirm("Lancer l'audit Drive des projets : verifier que chaque dossier projet contient bien toute l'arborescence (01_Admin, 02_Banque, 03_Urbanisme, 04_Travaux, CLIENT/* etc) et creer les sous-dossiers manquants ?")) return;
    setProjectsAuditing(true);
    setProjectsError('');
    setProjectsReport(null);
    try {
      const r = await axios.post(`${API}/drive/projects/audit-repair`);
      setProjectsReport(r.data);
    } catch (e) {
      setProjectsError(e.response?.data?.detail || 'Erreur audit');
    } finally {
      setProjectsAuditing(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto -mt-4 px-8" data-testid="maintenance-section">
      <div className="realease-card p-6">
        <h3 className="font-semibold text-gray-900 mb-2">Audit & reparation des dossiers clients Drive</h3>
        <p className="text-sm text-gray-500 mb-5">
          Verifie que tous les dossiers Client (<code>Client - Prenom NOM/</code>) presents sur Drive ont bien
          les deux sous-dossiers attendus : <b>Doc admins clients</b> et <b>Projets</b>. Si l'un d'eux est
          manquant, il est cree automatiquement.
        </p>
        <button
          onClick={runAudit}
          disabled={auditing}
          className="realease-btn-primary"
          data-testid="run-drive-audit"
        >
          {auditing ? "Audit en cours..." : "Lancer l'audit Drive"}
        </button>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        {report && (
          <div className="mt-5 space-y-3" data-testid="audit-report">
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-gray-900">{report.total}</div>
                <div className="text-[10px] uppercase text-gray-500">Dossiers audites</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-emerald-700">{report.ok}</div>
                <div className="text-[10px] uppercase text-gray-500">Conformes</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-amber-700">{report.repaired}</div>
                <div className="text-[10px] uppercase text-gray-500">Repares</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-red-700">{report.broken}</div>
                <div className="text-[10px] uppercase text-gray-500">Casses</div>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr><th className="p-2 text-left">Dossier</th><th className="p-2">Statut</th><th className="p-2 text-left">Detail</th></tr>
                </thead>
                <tbody>
                  {report.details.map((d, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2 truncate max-w-xs">{d.folder_name || d.lead_id}</td>
                      <td className="p-2 text-center">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          d.status === 'ok' ? 'bg-emerald-100 text-emerald-700' :
                          d.status === 'repaired' ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-700'
                        }`}>{d.status}</span>
                      </td>
                      <td className="p-2 text-xs text-gray-600">
                        {d.created.length > 0 && <span>Cree : {d.created.join(', ')}</span>}
                        {d.broken.length > 0 && <span className="text-red-600">{d.broken.join(' ; ')}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Audit dossiers projet (arborescence interne) */}
      <div className="realease-card p-6 mt-4">
        <h3 className="font-semibold text-gray-900 mb-2">Audit & reparation des dossiers projet Drive</h3>
        <p className="text-sm text-gray-500 mb-5">
          Verifie que chaque dossier projet contient bien l'arborescence complete
          (<code>01_Admin</code>, <code>02_Banque</code>, <code>03_Urbanisme</code>, <code>04_Travaux</code>,
          <code>05_Notaire</code>, <code>06_Assurance</code>, <code>CLIENT/Devis</code>, <code>CLIENT/Factures</code>...).
          Les sous-dossiers manquants sont crees automatiquement.
        </p>
        <button
          onClick={runProjectsAudit}
          disabled={projectsAuditing}
          className="realease-btn-primary"
          data-testid="run-drive-projects-audit"
        >
          {projectsAuditing ? "Audit en cours..." : "Lancer l'audit projets"}
        </button>
        {projectsError && <p className="mt-3 text-sm text-red-600">{projectsError}</p>}
        {projectsReport && (
          <div className="mt-5 space-y-3" data-testid="projects-audit-report">
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-gray-900">{projectsReport.total}</div>
                <div className="text-[10px] uppercase text-gray-500">Projets audites</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-emerald-700">{projectsReport.ok}</div>
                <div className="text-[10px] uppercase text-gray-500">Conformes</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-amber-700">{projectsReport.repaired}</div>
                <div className="text-[10px] uppercase text-gray-500">Repares</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-red-700">{projectsReport.broken}</div>
                <div className="text-[10px] uppercase text-gray-500">Casses</div>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr><th className="p-2 text-left">Projet</th><th className="p-2">Statut</th><th className="p-2 text-left">Sous-dossiers crees</th></tr>
                </thead>
                <tbody>
                  {projectsReport.details.map((d, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2 truncate max-w-xs">{d.name || d.project_id}</td>
                      <td className="p-2 text-center">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                          d.status === 'ok' ? 'bg-emerald-100 text-emerald-700' :
                          d.status === 'repaired' ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-700'
                        }`}>{d.status}</span>
                      </td>
                      <td className="p-2 text-xs text-gray-600">
                        {d.created && d.created.length > 0 && <span>{d.created.join(', ')}</span>}
                        {d.error && <span className="text-red-600">{d.error}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ParametresPage;
